import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Skeleton,
  TablePagination,
  Avatar,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import ToastMessage from "./toast-message";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Cancel } from "@mui/icons-material";

const PendingEditorialBoard = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '' });
  const [actionStatus, setActionStatus] = useState({ type: 0, message: "" });

  // Pagination state
  const [page, setPage] = useState(0);
  const [reviewersPerPage] = useState(10); // Fixed at 10 per page

  const clearToastMessage = () => setActionStatus({ type: 0, message: "" });

  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchPendingPeerReviewApps = async () => {
    setFetching(true);
    try {
      const response = await axios.get(`https://api.wajls.com/admin/v1/get_editorial_members.php?status=0`);

      setReviewers(response.data.members || []);
    } catch (error) {
      console.error("Error fetching reviewers:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchPendingPeerReviewApps();
  }, []);

  const parseJson = (jsonStr) => {
    try {
      return JSON.parse(jsonStr);
    } catch {
      return {};
    }
  };

  const handleConfirm = async () => {
    setDetailsOpen(false);
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    setLoading(true);
  
    try {
      const url = confirmDialog.action === "accept"
        ? "https://api.wajls.com/admin/v1/accept_editorial_member.php"
        : `https://api.wajls.com/admin/v1/reject_editorial_member.php?notify=${confirmDialog.action === "reject" ? 1 : 0}`;
  
      const response = await axios.post(url, {
        id: selectedReviewer.id,
        email: parseJson(selectedReviewer.personal_information).email,
        name: parseJson(selectedReviewer.personal_information).name
      });

      setActionStatus({
        type: response.data.success ? 1 : 0,
        message: response.data.message
      });

      if (response.data.success) {
        setReviewers(prev => prev.filter(r => r.id !== selectedReviewer.id));
      }
  
    } catch (error) {
      setActionStatus({ type: 2, message: "An error occurred" });
    } finally {
      setSelectedReviewer(null);
      setLoading(false);
    }
  };
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const paginatedReviewers = reviewers.slice(
    page * reviewersPerPage,
    page * reviewersPerPage + reviewersPerPage
  );
  
  return (
    <Box sx={{ mt: 4, maxWidth: "lg", margin: "auto" }}>
      <Typography variant="h5" gutterBottom>Pending Peer Review Applications</Typography>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          {fetching ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : reviewers.length > 0 ? (
            <>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f4f4f4" }}>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell align="center"><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReviewers.map((rev) => {
                    const personal = parseJson(rev.personal_information);
                    return (
                      <TableRow key={rev.id} hover>
                        <TableCell>{personal.name}</TableCell>
                        <TableCell>{personal.email}</TableCell>
                        <TableCell>{personal.phone}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              setSelectedReviewer(rev);
                              setDetailsOpen(true);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={reviewers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={reviewersPerPage}
                rowsPerPageOptions={[]} // Hide rowsPerPage select
              />
            </>
          ) : (
            <Typography sx={{ p: 2 }}>No pending applications found</Typography>
          )}
        </Box>
      </Paper>

      {/* Full Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => {setDetailsOpen(false); setSelectedReviewer(null)}} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
          Application Details
          <IconButton
            aria-label="close"
            onClick={() => {
              setSelectedReviewer(null);
              setDetailsOpen(false);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Cancel />
          </IconButton>
        </DialogTitle>

        {selectedReviewer && (
          <DialogContent dividers>
            {(() => {
              const personal = parseJson(selectedReviewer.personal_information);
              const academic = parseJson(selectedReviewer.academic_background);
              const editorial = parseJson(selectedReviewer.publication_and_editorial_experience);

              return (
                <>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      src={personal.profile_image}
                      alt={personal.name}
                      sx={{ width: 80, height: 80 }}
                    />
                    <Box>
                      <Typography variant="h6">{personal.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {personal.title} at {personal.affiliation}
                      </Typography>
                    </Box>
                  </Box>

                  {/* <Typography variant="subtitle1" gutterBottom>Personal Information</Typography> */}
                  <Typography>Name: {personal.name}</Typography>
                  <Typography>Title: {personal.title}</Typography>
                  <Typography>Affiliation: {personal.affiliation}</Typography>
                  <Typography>Department: {personal.department}</Typography>
                  <Typography>Email: {personal.email}</Typography>
                  <Typography>Phone: {personal.phone}</Typography>

                  <Box my={2}><hr /></Box>

                  <Typography variant="subtitle1" gutterBottom>Academic Background</Typography>
                  <Typography>Highest Degree: {academic.highest_degree}</Typography>
                  <Typography>Expertise: {academic.areas_of_expertise}</Typography>
                  <Typography>Experience: {academic.years_of_experience} years</Typography>

                  <Box my={2}><hr /></Box>

                  <Typography variant="subtitle1" gutterBottom>Publication & Editorial Experience</Typography>
                  <Typography>Papers Published: {editorial.number_of_papers_published}</Typography>
                  <Typography>Editorial Experience: {editorial.editorial_experience || 'None'}</Typography>

                  {editorial?.cv && (
                      <Box mt={2}>
                        <Typography variant="subtitle1" gutterBottom>
                          Curriculum Vitae
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            href={editorial.cv}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View CV
                          </Button>
                        </Box>
                      </Box>
                    )}

                  <Box my={2}><hr /></Box>

                  <Typography variant="subtitle1" gutterBottom>Available For</Typography>
                  <Typography>{selectedReviewer.availability_for.replace(/,/g, ", ")}</Typography>

                  <Typography variant="body2" sx={{ mt: 2, color: "gray" }}>
                    Applied on: {selectedReviewer.application_date}
                  </Typography>
                </>
              );
            })()}
          </DialogContent>
        )}
        <DialogActions>
          <Button
            color="success"
            variant="contained"
            onClick={() =>
              setConfirmDialog({ open: true, action: "accept"})
            }
          >
            Accept
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              setConfirmDialog({ open: true, action: "reject"})
            }
          >
            Reject
          </Button>
          <Button
            color="error"
            variant="outlined"
            onClick={() =>
              setConfirmDialog({ open: true, action: "delete"})
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: ''})}
      >
        <DialogTitle>Confirm {confirmDialog.action === "accept" ? "Acceptance" ? "reject" : "Rejection" : "Deletion"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} this peer review application?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', reviewerId: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color={confirmDialog.action === "accept" ? "success" : "error"} variant="contained">
            Yes, {confirmDialog.action}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || fetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {actionStatus.message && (
        <ToastMessage
          type={actionStatus.type}
          message={actionStatus.message}
          onClose={clearToastMessage}
        />
      )}
    </Box>
  );
};

export default PendingEditorialBoard;